import { useSyncExternalStore } from "react";
import {
  getStaticNetworkHealth,
  subscribeToStaticNetworkHealth,
  type StaticNetworkHealth,
} from "@-/dev.improve.haniverse.lib.static-data-health";
import { useContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";

/**
 * Whether the static-data NETWORK path is currently degraded in a way worth telling the user about.
 *
 * Two gates, both required:
 *
 * 1. The app is in `online` static-data mode. In `offline` mode reference data is read from a local
 *    file, so a slow screen has nothing to do with the network and offering pack downloads would be
 *    nonsense (it is already on).
 * 2. The worker transport has actually observed a failed or slow round-trip
 *    (`@-/dev.improve.haniverse.lib.static-data-health`). Nothing local ever reports there, so this
 *    cannot fire because a local query, a migration, or an FSRS scan was slow.
 *
 * The result is intentionally hard to reach: during healthy use — including a genuinely slow local
 * load — it stays `false` and the UI shows its ordinary spinner with no extra copy.
 */
export function useSlowStaticNetwork(): StaticNetworkHealth {
  const { isOnline } = useContentAvailability();
  const health = useSyncExternalStore(
    subscribeToStaticNetworkHealth,
    getStaticNetworkHealth,
    getStaticNetworkHealth,
  );

  if (!isOnline) {
    return { isDegraded: false, reason: null };
  }
  return health;
}
